/**
 * Owner's preview of everything this device sends out (Settings → Co wysłałem).
 * Every fetch() and WebSocket message is recorded on the device itself (IndexedDB), never on a server.
 * Bounded: 7 days, 200 entries, the newest 50 recordings. The token is never written down.
 */

export interface OutboundEntry {
  id?: number;
  at: number;
  /** "ws", "http". */
  kind: string;
  /** Destination without the token, e.g. "squire.….workers.dev /api/stt". */
  dest: string;
  method: string;
  bytes: number;
  /** Exact text that left the device (JSON, text), or a description for binary data. */
  body: string;
  /** Short answer for small JSON responses (e.g. the transcript from /api/stt). */
  response?: string;
  hasAudio?: boolean;
}

const DB_NAME = "squire-outbound";
const MAX_AGE_MS = 7 * 86_400_000;
const MAX_ENTRIES = 200;
const MAX_AUDIO = 50;
const MAX_RESPONSE = 20_000;

let dbPromise: Promise<IDBDatabase | null> | null = null;
/** Heartbeats are not stored one by one (one every 25 s), only counted. */
export let heartbeats = 0;

function open(): Promise<IDBDatabase | null> {
  dbPromise ??= new Promise((resolve) => {
    try {
      const req = indexedDB.open(DB_NAME, 1);
      req.onupgradeneeded = () => {
        req.result.createObjectStore("entries", { keyPath: "id", autoIncrement: true });
        req.result.createObjectStore("audio");
      };
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => resolve(null);
    } catch {
      resolve(null);
    }
  });
  return dbPromise;
}

const done = (tx: IDBTransaction) =>
  new Promise<void>((resolve) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => resolve();
    tx.onabort = () => resolve();
  });

async function record(entry: OutboundEntry, audio?: Blob): Promise<void> {
  const db = await open();
  if (!db) return;
  const tx = db.transaction(["entries", "audio"], "readwrite");
  const entries = tx.objectStore("entries");
  const add = entries.add({ ...entry, hasAudio: Boolean(audio) });
  if (audio) add.onsuccess = () => tx.objectStore("audio").put(audio, add.result);
  await done(tx);
  await prune(db);
}

async function prune(db: IDBDatabase): Promise<void> {
  const tx = db.transaction(["entries", "audio"], "readwrite");
  const entries = tx.objectStore("entries");
  const audio = tx.objectStore("audio");
  const cutoff = Date.now() - MAX_AGE_MS;
  const count = entries.count();
  count.onsuccess = () => {
    let excess = count.result - MAX_ENTRIES;
    const cur = entries.openCursor();
    cur.onsuccess = () => {
      const c = cur.result;
      if (!c) return;
      const e = c.value as OutboundEntry;
      if (excess > 0 || e.at < cutoff) {
        excess--;
        audio.delete(c.key);
        c.delete();
        c.continue();
      }
    };
  };
  const audioCount = audio.count();
  audioCount.onsuccess = () => {
    let excess = audioCount.result - MAX_AUDIO;
    if (excess <= 0) return;
    const cur = audio.openKeyCursor();
    cur.onsuccess = () => {
      const c = cur.result;
      if (!c || excess-- <= 0) return;
      audio.delete(c.primaryKey);
      c.continue();
    };
  };
  await done(tx);
}

export async function listOutbound(): Promise<OutboundEntry[]> {
  const db = await open();
  if (!db) return [];
  const tx = db.transaction("entries", "readonly");
  const req = tx.objectStore("entries").getAll();
  await done(tx);
  return ((req.result ?? []) as OutboundEntry[]).reverse();
}

export async function outboundAudio(id: number): Promise<Blob | null> {
  const db = await open();
  if (!db) return null;
  const tx = db.transaction("audio", "readonly");
  const req = tx.objectStore("audio").get(id);
  await done(tx);
  return (req.result as Blob | undefined) ?? null;
}

export async function clearOutbound(): Promise<void> {
  const db = await open();
  if (!db) return;
  const tx = db.transaction(["entries", "audio"], "readwrite");
  tx.objectStore("entries").clear();
  tx.objectStore("audio").clear();
  await done(tx);
}

/** Host and path without query secrets. */
export function describeUrl(raw: string): string {
  try {
    const u = new URL(raw, location.href);
    u.searchParams.delete("token");
    const q = u.searchParams.toString();
    return `${u.host} ${u.pathname}${q ? `?${q}` : ""}`;
  } catch {
    return raw.slice(0, 200);
  }
}

async function bodyText(
  body: BodyInit | null | undefined,
): Promise<{ text: string; bytes: number; audio?: Blob }> {
  if (body == null) return { text: "", bytes: 0 };
  if (typeof body === "string") return { text: body, bytes: new TextEncoder().encode(body).length };
  if (body instanceof Blob) {
    if (body.type.startsWith("audio/"))
      return {
        text: `nagranie ${body.type}, ${(body.size / 1024).toFixed(0)} KB`,
        bytes: body.size,
        audio: body,
      };
    if (body.type.includes("json") || body.type.startsWith("text/"))
      return { text: await body.text(), bytes: body.size };
    return { text: `dane binarne ${body.type || "?"}, ${body.size} B`, bytes: body.size };
  }
  if (body instanceof ArrayBuffer || ArrayBuffer.isView(body))
    return { text: `dane binarne, ${body.byteLength} B`, bytes: body.byteLength };
  return { text: `(${Object.prototype.toString.call(body)})`, bytes: 0 };
}

let installed = false;

/** Wraps fetch and WebSocket.send once, before the app starts. */
export function installOutboundLog(): void {
  if (installed || typeof window === "undefined") return;
  installed = true;
  const origFetch = window.fetch.bind(window);
  window.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
    const url = typeof input === "string" ? input : input instanceof URL ? input.href : input.url;
    const method = init?.method ?? (input instanceof Request ? input.method : "GET");
    const sent: { text: string; bytes: number; audio?: Blob } = await bodyText(init?.body).catch(() => ({
      text: "",
      bytes: 0,
    }));
    const base = {
      at: Date.now(),
      kind: "http",
      dest: describeUrl(url),
      method,
      bytes: sent.bytes,
      body: sent.text,
    };
    let res: Response;
    try {
      res = await origFetch(input, init);
    } catch (e) {
      void record({ ...base, response: "błąd sieci (mogło nie dojść)" }, sent.audio).catch(() => {});
      throw e;
    }
    let response: string | undefined;
    const type = res.headers.get("content-type") ?? "";
    const len = Number(res.headers.get("content-length") ?? 0);
    if (
      type.includes("json") &&
      len < MAX_RESPONSE &&
      !url.includes("/api/export") &&
      !url.includes("/api/llm-log")
    )
      response = await res
        .clone()
        .text()
        .then((t) => t.slice(0, MAX_RESPONSE))
        .catch(() => undefined);
    void record(
      { ...base, response: response !== undefined ? `${res.status} ${response}` : String(res.status) },
      sent.audio,
    ).catch(() => {});
    return res;
  };
  const origSend = WebSocket.prototype.send;
  WebSocket.prototype.send = function (this: WebSocket, data: Parameters<WebSocket["send"]>[0]) {
    if (typeof data === "string" && data === '{"t":"ping"}') heartbeats++;
    else
      void record({
        at: Date.now(),
        kind: "ws",
        dest: describeUrl(this.url),
        method: "send",
        bytes: typeof data === "string" ? new TextEncoder().encode(data).length : 0,
        body: typeof data === "string" ? data : "(dane binarne)",
      }).catch(() => {});
    return origSend.call(this, data);
  };
}
